import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import type {
  AccountDto,
  AccountStatsDto,
  AiGroupEmailDto,
  AssignVirtualFolderRequest,
  AiUsageDto,
  AppSettingsDto,
  DevTableDto,
  DevTableRowsDto,
  MailGridRowDto,
  CategorizeRequest,
  ClassifyStatusDto,
  CreateEventRequest,
  EventDto,
  FilterCountsDto,
  FilterDto,
  FolderDto,
  FolderRuleScanStatusDto,
  GeneratePromptRequest,
  GeneratePromptResponse,
  LabelMapDto,
  MailStatsDto,
  MessageDetailDto,
  MessageListDto,
  MessageSummaryDto,
  ModelInfoDto,
  PinEmailRequest,
  PushStatusDto,
  SaveFilterRequest,
  SaveSenderFilterRequest,
  SaveSenderGroupRequest,
  SaveVirtualFolderRequest,
  VirtualFolderDto,
  SenderFilterDto,
  SenderGroupDto,
  SendMailRequest,
  SyncLogEntryDto,
  SyncStatusDto,
  TestFilterRequest,
  TestFilterResponse,
} from "../../shared/types";

/** Error whose name marks the account as needing interactive re-login. */
export class ReauthError extends Error {
  override name = "ReauthError";
  /** Which mail account's tokens expired (username + provider), when known. */
  account?: string;
  provider?: string;
  constructor(message: string, account?: string, provider?: string) {
    super(message);
    this.account = account;
    this.provider = provider;
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // Attach the Clerk session token so the server can authenticate the user.
  const token = await (window as any).Clerk?.session?.getToken?.().catch(() => null);
  const headers = new Headers(init?.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(path, { ...init, headers });
  if (res.status === 401) {
    const body = await res.json().catch(() => ({}));
    // Account-level reauth (expired Graph/Google token) vs. app-level (Clerk).
    if (body.reauth) {
      throw new ReauthError(
        body.message ?? "Re-authentication required",
        body.account,
        body.provider,
      );
    }
    throw new Error(body.error ?? body.message ?? "Not signed in");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export function useAccounts() {
  return useQuery({
    queryKey: ["accounts"],
    queryFn: () => apiFetch<AccountDto[]>("/api/accounts"),
  });
}

export function useStats(accountId: number) {
  return useQuery({
    queryKey: ["stats", accountId],
    queryFn: () => apiFetch<AccountStatsDto>(`/api/accounts/${accountId}/stats`),
    refetchInterval: 60_000,
  });
}

/** Mail folders (Graph folders / Gmail labels); id 0 = combined virtual folders. */
export function useFolders(accountId: number, enabled = accountId > 0) {
  return useQuery({
    queryKey: ["folders", accountId],
    queryFn: () => apiFetch<FolderDto[]>(`/api/accounts/${accountId}/folders`),
    enabled: enabled && accountId >= 0,
    refetchInterval: 60_000,
  });
}

/** All loaded pages of one folder (null = inbox); cursor = opaque nextLink/pageToken. */
export function useInfiniteMessages(accountId: number, folderId: string | null, enabled = true) {
  return useInfiniteQuery({
    enabled,
    queryKey: ["messages", accountId, folderId ?? "inbox"],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (folderId) params.set("folder", folderId);
      // First page loads a full 100; "Load more" appends 50 at a time.
      if (pageParam) {
        params.set("next", pageParam);
        params.set("limit", "50");
      }
      const qs = params.toString();
      return apiFetch<MessageListDto>(`/api/accounts/${accountId}/messages${qs ? `?${qs}` : ""}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextLink,
    refetchInterval: 60_000,
  });
}

/** Flattened messages currently loaded for an account+folder (from the shared cache). */
export function loadedMessages(
  queryClient: ReturnType<typeof useQueryClient>,
  accountId: number,
  folderId: string | null,
) {
  const data = queryClient.getQueryData<InfiniteData<MessageListDto>>([
    "messages",
    accountId,
    folderId ?? "inbox",
  ]);
  return data?.pages.flatMap((p) => p.messages) ?? [];
}

export function useMessage(accountId: number, messageId: string | null) {
  return useQuery({
    queryKey: ["message", accountId, messageId],
    queryFn: () =>
      apiFetch<MessageDetailDto>(
        `/api/accounts/${accountId}/messages/${encodeURIComponent(messageId!)}`,
      ),
    enabled: messageId !== null,
  });
}

/** Events in [startISO, endISO) — the server passes the range to Graph/Google Calendar. */
export function useEventsRange(accountId: number, startISO: string, endISO: string) {
  return useQuery({
    queryKey: ["events", accountId, startISO, endISO],
    queryFn: () =>
      apiFetch<EventDto[]>(
        `/api/accounts/${accountId}/events?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}`,
      ),
    refetchInterval: 5 * 60_000,
  });
}

/** Create a calendar event on a specific account, then refresh its events. */
export function useCreateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, ...body }: CreateEventRequest & { accountId: number }) =>
      apiFetch<{ ok: boolean }>(`/api/accounts/${accountId}/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

/** Move/resize/edit an event, then refresh the calendar. */
export function useUpdateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      accountId,
      eventId,
      ...body
    }: CreateEventRequest & { accountId: number; eventId: string }) =>
      apiFetch<{ ok: boolean }>(
        `/api/accounts/${accountId}/events/${encodeURIComponent(eventId)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

export function useSendMail(accountId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: SendMailRequest) =>
      apiFetch<{ ok: boolean }>(`/api/accounts/${accountId}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stats", accountId] });
    },
  });
}

export function useSettings() {
  return useQuery({
    queryKey: ["settings"],
    queryFn: () => apiFetch<AppSettingsDto>("/api/settings"),
    staleTime: Infinity,
  });
}

export function useSaveSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (partial: Partial<AppSettingsDto>) =>
      apiFetch<AppSettingsDto>("/api/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(partial),
      }),
    onSuccess: (settings) => queryClient.setQueryData(["settings"], settings),
  });
}

export function useModels() {
  return useQuery({
    queryKey: ["models"],
    queryFn: () => apiFetch<ModelInfoDto[]>("/api/models"),
    staleTime: 30 * 60_000,
  });
}

export function useFilters() {
  return useQuery({
    queryKey: ["filters"],
    queryFn: () => apiFetch<FilterDto[]>("/api/filters"),
    staleTime: Infinity, // only changes through the settings dialog
  });
}

/** Create (id undefined) or update (id set) a filter. */
export function useSaveFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: SaveFilterRequest & { id?: number }) =>
      apiFetch<FilterDto>(id ? `/api/filters/${id}` : "/api/filters", {
        method: id ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["filters"] });
      // A prompt edit may have cleared cached labels server-side.
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
  });
}

export function useDeleteFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetch<{ ok: boolean }>(`/api/filters/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["filters"] });
      queryClient.invalidateQueries({ queryKey: ["labels"] });
    },
  });
}

// --- Sender-based filters (grouped) ---

export function useSenderGroups() {
  return useQuery({
    queryKey: ["sender-groups"],
    queryFn: () => apiFetch<SenderGroupDto[]>("/api/sender-groups"),
    staleTime: Infinity,
  });
}

export function useSaveSenderGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: SaveSenderGroupRequest & { id?: number }) =>
      apiFetch<SenderGroupDto>(id ? `/api/sender-groups/${id}` : "/api/sender-groups", {
        method: id ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sender-groups"] }),
  });
}

export function useDeleteSenderGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ ok: boolean }>(`/api/sender-groups/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sender-groups"] }),
  });
}

export function useSaveSenderFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: SaveSenderFilterRequest & { id?: number }) =>
      apiFetch<SenderFilterDto>(id ? `/api/sender-filters/${id}` : "/api/sender-filters", {
        method: id ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sender-groups"] }),
  });
}

export function useDeleteSenderFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ ok: boolean }>(`/api/sender-filters/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sender-groups"] }),
  });
}

/**
 * Run a sender-filter. `months` null (default) loads the most recent 50; a
 * month value widens the window to that range (up to 500).
 */
export function useSenderFilterMessages(
  accountId: number,
  filterId: number | null,
  months: number | null,
) {
  return useQuery({
    queryKey: ["sender-filter-messages", accountId, filterId, months ?? "recent"],
    queryFn: () => {
      // No months = everything cached (the store spans 12 months anyway).
      const params = new URLSearchParams();
      if (months != null) params.set("months", String(months));
      params.set("limit", "2000");
      return apiFetch<MessageListDto>(
        `/api/accounts/${accountId}/sender-filter/${filterId}/messages?${params}`,
      );
    },
    enabled: filterId != null,
    staleTime: 60_000,
  });
}

// --- Virtual folders (DB-only custom folders) ---

export function useVirtualFolders(enabled = true) {
  return useQuery({
    queryKey: ["virtual-folders"],
    queryFn: () => apiFetch<VirtualFolderDto[]>("/api/virtual-folders"),
    // Unread counts move server-side (sync, other views) — keep them fresh.
    refetchInterval: 60_000,
    enabled,
  });
}

/** Create (no id) or rename/recolor (id set) a virtual folder. */
export function useSaveVirtualFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: SaveVirtualFolderRequest & { id?: number }) =>
      apiFetch<VirtualFolderDto>(id ? `/api/virtual-folders/${id}` : "/api/virtual-folders", {
        method: id ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ["virtual-folders"] });
        // A saved rule scans history and moves mail out of the inbox views —
        // let those lists refetch as it lands.
        queryClient.invalidateQueries({ queryKey: ["messages"] });
        queryClient.invalidateQueries({ queryKey: ["virtual-folder-messages"] });
      };
      invalidate();
      // Address scans are awaited server-side; AI scans run detached — keep
      // refreshing counts until the scan reports done (bounded at 10 min).
      const poll = setInterval(() => {
        apiFetch<FolderRuleScanStatusDto>("/api/virtual-folders/scan-status")
          .then((s) => {
            invalidate();
            if (!s.running) clearInterval(poll);
          })
          .catch(() => clearInterval(poll));
      }, 2500);
      setTimeout(() => clearInterval(poll), 10 * 60_000);
    },
  });
}

export function useDeleteVirtualFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ ok: boolean }>(`/api/virtual-folders/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["virtual-folders"] });
      // Its mail reappears in the provider views.
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      queryClient.invalidateQueries({ queryKey: ["folders"] });
    },
  });
}

export function useVirtualFolderMessages(folderId: number | null) {
  return useQuery({
    queryKey: ["virtual-folder-messages", folderId],
    queryFn: () => apiFetch<MessageListDto>(`/api/virtual-folders/${folderId}/messages`),
    enabled: folderId != null,
    staleTime: 60_000,
  });
}

/**
 * Every folder's message list at once (inbox date-group sub-panels). Same
 * query keys/fns as useVirtualFolderMessages, so caches are shared with the
 * folder views and the Analyze tab.
 */
export function useAllFolderMessages(folderIds: number[], enabled: boolean) {
  return useQueries({
    queries: folderIds.map((id) => ({
      queryKey: ["virtual-folder-messages", id],
      queryFn: () => apiFetch<MessageListDto>(`/api/virtual-folders/${id}/messages`),
      enabled,
      staleTime: 60_000,
    })),
    combine: (results) =>
      results.map((r, i) => ({ folderId: folderIds[i]!, messages: r.data?.messages ?? [] })),
  });
}

/** Assign (move) one or many mails into a virtual folder. messageIds are RAW. */
export function useAssignToVirtualFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, ...body }: AssignVirtualFolderRequest & { folderId: number }) =>
      apiFetch<{ ok: boolean }>(`/api/virtual-folders/${folderId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: (_, { messages }) => {
      // Move semantics: drop the mail from every provider-view cache at once.
      const candidates = new Set(
        messages.flatMap((m) => [m.messageId, `${m.accountId}:${m.messageId}`]),
      );
      queryClient.setQueriesData<InfiniteData<MessageListDto>>(
        { queryKey: ["messages"] },
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((p) => ({
                  ...p,
                  messages: p.messages.filter((m) => !candidates.has(m.id)),
                })),
              }
            : old,
      );
      queryClient.invalidateQueries({ queryKey: ["virtual-folders"] });
      queryClient.invalidateQueries({ queryKey: ["virtual-folder-messages"] });
      queryClient.invalidateQueries({ queryKey: ["folders"] });
    },
  });
}

export function useUnassignFromVirtualFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      folderId,
      accountId,
      messageId,
    }: {
      folderId: number;
      accountId: number;
      messageId: string;
    }) =>
      apiFetch<{ ok: boolean }>(
        `/api/virtual-folders/${folderId}/messages/${encodeURIComponent(messageId)}?account=${accountId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["virtual-folders"] });
      queryClient.invalidateQueries({ queryKey: ["virtual-folder-messages"] });
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      queryClient.invalidateQueries({ queryKey: ["folders"] });
    },
  });
}

export function useLabels(accountId: number, enabled = true) {
  return useQuery({
    queryKey: ["labels", accountId],
    queryFn: () => apiFetch<LabelMapDto>(`/api/accounts/${accountId}/labels`),
    // The SERVER also categorizes (new-mail watcher syncs, backfills), so the
    // cache must refresh on its own — it's a cheap local-store read.
    refetchInterval: 60_000,
    enabled,
  });
}

/** Local mail-store statistics for the dashboard (counts, storage, coverage). */
export function useMailStats() {
  return useQuery({
    queryKey: ["mail-stats"],
    queryFn: () => apiFetch<MailStatsDto>("/api/mail-stats"),
    refetchInterval: 60_000,
  });
}

/** AI Group studio: write a classification prompt from a plain description. */
export function useGeneratePrompt() {
  return useMutation({
    mutationFn: (body: GeneratePromptRequest) =>
      apiFetch<GeneratePromptResponse>("/api/ai/generate-prompt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
  });
}

/** Conversation for one message (subject-normalized, incl. own Sent replies). */
export function useThread(accountId: number, messageId: string | null) {
  return useQuery({
    queryKey: ["thread", accountId, messageId],
    queryFn: () =>
      apiFetch<{ messages: MessageSummaryDto[] }>(
        `/api/accounts/${accountId}/messages/${encodeURIComponent(messageId!)}/thread`,
      ),
    enabled: messageId !== null,
    staleTime: 60_000,
  });
}

// --- Manual AI-category pins ---

export function useAiGroupEmails() {
  return useQuery({
    queryKey: ["ai-group-emails"],
    queryFn: () => apiFetch<AiGroupEmailDto[]>("/api/ai-group-emails"),
  });
}

function usePinInvalidations() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["ai-group-emails"] });
    queryClient.invalidateQueries({ queryKey: ["labels"] });
    queryClient.invalidateQueries({ queryKey: ["filter-counts"] });
  };
}

/** Pin an email to an AI group (enforced verdict; the LLM is skipped for it). */
export function usePinEmail() {
  const invalidate = usePinInvalidations();
  return useMutation({
    mutationFn: (body: PinEmailRequest) =>
      apiFetch<{ ok: boolean }>("/api/ai-group-emails", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

export function useUpdatePin() {
  const invalidate = usePinInvalidations();
  return useMutation({
    mutationFn: ({ id, filterId }: { id: number; filterId: number }) =>
      apiFetch<{ ok: boolean }>(`/api/ai-group-emails/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filterId }),
      }),
    onSuccess: invalidate,
  });
}

export function useDeletePin() {
  const invalidate = usePinInvalidations();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ ok: boolean }>(`/api/ai-group-emails/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

/** OpenRouter spend totals (Settings → AI). */
export function useAiUsage() {
  return useQuery({
    queryKey: ["ai-usage"],
    queryFn: () => apiFetch<AiUsageDto>("/api/ai/usage"),
    refetchInterval: 60_000,
  });
}

/** "Save and run": classify the stored history for one AI group (detached). */
export function useClassifyBackfill() {
  return useMutation({
    mutationFn: (body: { filterId: number; override?: boolean }) =>
      apiFetch<ClassifyStatusDto>("/api/ai/classify-backfill", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
  });
}

/** Progress of the history classification — drives the progress panel. */
export function useClassifyStatus() {
  return useQuery({
    queryKey: ["classify-status"],
    queryFn: () => apiFetch<ClassifyStatusDto>("/api/ai/classify-status"),
    refetchInterval: (query) => (query.state.data?.running ? 2000 : 30_000),
  });
}

/** AI Group studio: dry-run a prompt against recent mail (nothing persisted). */
export function useTestFilter() {
  return useMutation({
    mutationFn: (body: TestFilterRequest) =>
      apiFetch<TestFilterResponse>("/api/ai/test-filter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
  });
}

/**
 * Live view of the background sync across all accounts — drives the progress
 * panel. Polls fast while a sync runs, lazily otherwise.
 */
export function useSyncStatus() {
  return useQuery({
    queryKey: ["sync-status"],
    queryFn: () => apiFetch<SyncStatusDto>("/api/accounts/0/sync-status"),
    refetchInterval: (query) => (query.state.data?.running ? 2000 : 15_000),
  });
}

/** Explicit mark-as-read; also flips the cached message lists immediately. */
export function useMarkRead(accountId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      apiFetch<{ ok: boolean }>(
        `/api/accounts/${accountId}/messages/${encodeURIComponent(messageId)}/read`,
        { method: "POST" },
      ),
    // Optimistic: the row restyles the moment it's clicked; the provider
    // request runs in the background and a failure refetches the truth.
    onMutate: (messageId) => {
      queryClient.setQueriesData<InfiniteData<MessageListDto>>({ queryKey: ["messages"] }, (data) =>
        data
          ? {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                messages: page.messages.map((m) =>
                  m.id === messageId ? { ...m, isRead: true } : m,
                ),
              })),
            }
          : data,
      );
      // Sidebar folder badges: decrement the owning folder's unread count
      // immediately (before the flip below marks the row read) — the
      // invalidation refetch confirms, but shouldn't gate the visible update.
      for (const [key, data] of queryClient.getQueriesData<MessageListDto>({
        queryKey: ["virtual-folder-messages"],
      })) {
        if (!data?.messages.some((m) => m.id === messageId && !m.isRead)) continue;
        const folderId = key[1] as number;
        queryClient.setQueryData<VirtualFolderDto[]>(["virtual-folders"], (old) =>
          old?.map((f) =>
            f.id === folderId ? { ...f, unreadCount: Math.max(0, f.unreadCount - 1) } : f,
          ),
        );
      }
      // Flat list views (virtual folders) show read state too.
      queryClient.setQueriesData<MessageListDto>(
        { queryKey: ["virtual-folder-messages"] },
        (old) =>
          old
            ? {
                ...old,
                messages: old.messages.map((m) =>
                  m.id === messageId ? { ...m, isRead: true } : m,
                ),
              }
            : old,
      );
    },
    onSettled: (_data, error) => {
      // A failed request flips the row back via refetch.
      if (error) {
        queryClient.invalidateQueries({ queryKey: ["messages"] });
        queryClient.invalidateQueries({ queryKey: ["virtual-folder-messages"] });
      }
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["virtual-folders"] });
    },
  });
}

/** Server-side sync event log; polled only while the Details dialog is open. */
export function useSyncLog(enabled: boolean) {
  return useQuery({
    queryKey: ["sync-log"],
    queryFn: () => apiFetch<SyncLogEntryDto[]>("/api/sync-log"),
    enabled,
    refetchInterval: enabled ? 2000 : false,
  });
}

/** Provider push subscription state (Details dialog only). */
export function usePushStatus(enabled: boolean) {
  return useQuery({
    queryKey: ["push-status"],
    queryFn: () => apiFetch<PushStatusDto[]>("/api/push-status"),
    enabled,
    refetchInterval: enabled ? 30_000 : false,
  });
}

/**
 * Kick the background mail sync (202 + detached job) and poll until it goes
 * idle, streaming fresh rows into the UI along the way. `accountId` 0 = all.
 */
export function useSync(accountId: number) {
  const queryClient = useQueryClient();
  const refreshView = () => {
    queryClient.invalidateQueries({ queryKey: ["messages"] });
    queryClient.invalidateQueries({ queryKey: ["stats"] });
    queryClient.invalidateQueries({ queryKey: ["labels"] });
  };
  return useMutation({
    mutationFn: async (opts?: { force?: boolean; reconcile?: boolean }) => {
      const params = new URLSearchParams();
      if (opts?.force) params.set("force", "1");
      // Deep sync: full-window diff against the provider (deletions/moves).
      if (opts?.reconcile) params.set("reconcile", "1");
      const qs = params.size ? `?${params}` : "";
      let status = await apiFetch<SyncStatusDto>(`/api/accounts/${accountId}/sync${qs}`, {
        method: "POST",
      });
      // Poll (max ~4 min — the server time-boxes runs below that); refresh the
      // visible lists every ~10s so a long first backfill streams in.
      for (let i = 0; status.running && i < 120; i++) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        status = await apiFetch<SyncStatusDto>(`/api/accounts/${accountId}/sync-status`);
        if (i % 5 === 4) refreshView();
      }
      return status;
    },
    onSettled: () => {
      refreshView();
      queryClient.invalidateQueries({ queryKey: ["folders"] });
      queryClient.invalidateQueries({ queryKey: ["filter-counts"] });
      queryClient.invalidateQueries({ queryKey: ["sender-filter-messages"] });
      queryClient.invalidateQueries({ queryKey: ["virtual-folders"] });
      queryClient.invalidateQueries({ queryKey: ["virtual-folder-messages"] });
    },
  });
}

/** filterId → matched-message count for one account (from cached labels). */
export function useFilterCounts(accountId: number, enabled = true) {
  return useQuery({
    queryKey: ["filter-counts", accountId],
    queryFn: () => apiFetch<FilterCountsDto>(`/api/accounts/${accountId}/filter-counts`),
    refetchInterval: 60_000, // server-side categorization changes these too
    enabled,
  });
}

/** Sends every currently loaded message page to the categorizer. */
export function useCategorize(accountId: number, folderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (filterIds: number[]) => {
      const request: CategorizeRequest = {
        filterIds,
        messages: loadedMessages(queryClient, accountId, folderId).map((m) => ({
          id: m.id,
          subject: m.subject,
          from: m.from ? `${m.from.name} <${m.from.address}>` : "unknown",
          preview: m.bodyPreview.slice(0, 120),
        })),
      };
      return apiFetch<LabelMapDto>(`/api/accounts/${accountId}/categorize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
    },
    onSuccess: (labels) => {
      queryClient.setQueryData(["labels", accountId], labels);
    },
  });
}

export interface SearchCriteria {
  from?: string;
  subject?: string;
  body?: string;
}

export function useSearchMessages(accountId: number) {
  return useMutation({
    mutationFn: (criteria: SearchCriteria) => {
      const params = new URLSearchParams();
      if (criteria.from) params.set("from", criteria.from);
      if (criteria.subject) params.set("subject", criteria.subject);
      if (criteria.body) params.set("body", criteria.body);
      return apiFetch<MessageListDto>(`/api/accounts/${accountId}/search?${params}`);
    },
  });
}

/** AI search over the currently loaded message pages; returns matching summaries. */
export function useAiSearch(accountId: number, folderId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (prompt: string) => {
      const loaded = loadedMessages(queryClient, accountId, folderId);
      const { ids } = await apiFetch<{ ids: string[] }>(`/api/accounts/${accountId}/ai-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          messages: loaded.map((m) => ({
            id: m.id,
            subject: m.subject,
            from: m.from ? `${m.from.name} <${m.from.address}>` : "unknown",
            preview: m.bodyPreview.slice(0, 120),
          })),
        }),
      });
      const idSet = new Set(ids);
      return loaded.filter((m) => idSet.has(m.id));
    },
  });
}

/** Move a message to Trash / Deleted Items and prune it from the loaded list. */
export function useDeleteMessage(accountId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      apiFetch<{ ok: boolean }>(
        `/api/accounts/${accountId}/messages/${encodeURIComponent(messageId)}`,
        { method: "DELETE" },
      ),
    // Optimistic: the row leaves every list cache immediately; the provider
    // delete runs in the background and a failure restores it via refetch.
    onMutate: (messageId) => {
      // The same message lives under two ids: raw in its account's caches and
      // "accountId:raw" in the combined All view. Prune both everywhere.
      const candidates = new Set([messageId]);
      if (accountId === 0) {
        const sep = messageId.indexOf(":");
        if (sep > 0) candidates.add(messageId.slice(sep + 1));
      } else {
        candidates.add(`${accountId}:${messageId}`);
      }
      queryClient.setQueriesData<InfiniteData<MessageListDto>>(
        { queryKey: ["messages"] },
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((p) => ({
                  ...p,
                  messages: p.messages.filter((m) => !candidates.has(m.id)),
                })),
              }
            : old,
      );
      queryClient.setQueriesData<MessageListDto>({ queryKey: ["virtual-folder-messages"] }, (old) =>
        old ? { ...old, messages: old.messages.filter((m) => !candidates.has(m.id)) } : old,
      );
      pruneSenderFilterCache(queryClient, candidates);
    },
    onSettled: (_data, error, messageId) => {
      let realAccountId = accountId;
      if (accountId === 0) {
        const sep = messageId.indexOf(":");
        if (sep > 0) realAccountId = Number(messageId.slice(0, sep)) || 0;
      }
      // A failed delete brings the row back with the refetch.
      if (error) {
        queryClient.invalidateQueries({ queryKey: ["messages"] });
        queryClient.invalidateQueries({ queryKey: ["virtual-folder-messages"] });
      }
      queryClient.invalidateQueries({ queryKey: ["stats", realAccountId] });
      queryClient.invalidateQueries({ queryKey: ["folders", realAccountId] });
      queryClient.invalidateQueries({ queryKey: ["virtual-folders"] });
    },
  });
}

/** Drop deleted ids from flat MessageListDto caches (sender-filter + virtual-folder views). */
function pruneSenderFilterCache(
  queryClient: ReturnType<typeof useQueryClient>,
  candidates: Set<string>,
) {
  for (const key of ["sender-filter-messages", "virtual-folder-messages"]) {
    queryClient.setQueriesData<MessageListDto>({ queryKey: [key] }, (old) =>
      old ? { ...old, messages: old.messages.filter((m) => !candidates.has(m.id)) } : old,
    );
  }
}

/**
 * Delete several messages in one request (a single IMAP connection server-side).
 * Prunes the deleted ids from every message cache and returns which ids failed.
 */
export function useBulkDeleteMessages(accountId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiFetch<{ deleted: string[]; failed: string[] }>(
        `/api/accounts/${accountId}/messages/bulk-delete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids }),
        },
      ),
    // Optimistic: the rows leave every list cache the moment the request is
    // fired, so the UI never blocks on a slow provider delete.
    onMutate: (ids) => {
      // Each message may live under two ids (raw + "accountId:raw" combined).
      const candidates = new Set<string>();
      for (const id of ids) {
        candidates.add(id);
        if (accountId === 0) {
          const sep = id.indexOf(":");
          if (sep > 0) candidates.add(id.slice(sep + 1));
        } else {
          candidates.add(`${accountId}:${id}`);
        }
      }
      queryClient.setQueriesData<InfiniteData<MessageListDto>>(
        { queryKey: ["messages"] },
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((p) => ({
                  ...p,
                  messages: p.messages.filter((m) => !candidates.has(m.id)),
                })),
              }
            : old,
      );
      queryClient.setQueriesData<MessageListDto>({ queryKey: ["virtual-folder-messages"] }, (old) =>
        old ? { ...old, messages: old.messages.filter((m) => !candidates.has(m.id)) } : old,
      );
      pruneSenderFilterCache(queryClient, candidates);
    },
    onSettled: (data, error) => {
      // Anything the server couldn't delete (or everything, if the request
      // died) reappears with the refetch — nothing is silently lost.
      if (error || data?.failed.length) {
        queryClient.invalidateQueries({ queryKey: ["messages"] });
        queryClient.invalidateQueries({ queryKey: ["virtual-folder-messages"] });
      }
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["folders"] });
      queryClient.invalidateQueries({ queryKey: ["virtual-folders"] });
    },
  });
}

/** Connect (or reconnect) an IMAP mailbox with an app password. */
export function useConnectImap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: import("../../shared/types").ConnectImapRequest) =>
      apiFetch<{ id: number }>("/api/imap-accounts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountId: number) =>
      apiFetch<{ ok: boolean }>(`/api/accounts/${accountId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}

// --- dev tools (owner-only; server enforces with 403) ---

export function useDevTables(enabled: boolean) {
  return useQuery({
    queryKey: ["dev-tables"],
    queryFn: () => apiFetch<DevTableDto[]>("/api/dev/tables"),
    enabled,
    staleTime: 30_000,
  });
}

export function useDevTableRows(table: string | null) {
  return useQuery({
    queryKey: ["dev-table-rows", table],
    queryFn: () =>
      apiFetch<DevTableRowsDto>(`/api/dev/tables/${encodeURIComponent(table!)}/rows`),
    enabled: table !== null,
    staleTime: 30_000,
  });
}

/** Header dot: is the server's database reachable? Polled every minute. */
export function useDbHealth() {
  return useQuery({
    queryKey: ["db-health"],
    queryFn: () => apiFetch<{ ok: boolean }>("/api/db-health"),
    refetchInterval: 60_000,
    retry: false, // a failed probe should show red now, not after 3 retries
  });
}

/** "Search emails" grid rows (local store, deleted/junk excluded). */
export function useMailGrid(enabled: boolean) {
  return useQuery({
    queryKey: ["mail-grid"],
    queryFn: () => apiFetch<MailGridRowDto[]>("/api/mail-grid"),
    enabled,
    staleTime: 60_000,
  });
}
